import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const F = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    {hint && <span className="block text-xs text-slate-500 mt-0.5">{hint}</span>}
    <div className="mt-1.5">{children}</div>
  </label>
);
const I = (p) => <input {...p} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40" />;
const TA = (p) => <textarea {...p} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40" />;

const SiteSettings = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cms/site-settings`).then((r) => setS(r.data)).catch(() => toast.error("Failed to load settings"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cms/site-settings`, s);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <div className="text-slate-400">Loading…</div>;

  const patch = (key, val) => setS({ ...s, [key]: val });
  const patchSocial = (k, v) => setS({ ...s, social: { ...s.social, [k]: v } });
  const patchSEO = (k, v) => setS({ ...s, seo: { ...s.seo, [k]: v } });

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Site Settings</h1>
          <p className="text-sm text-slate-500">Global brand, contact, social, and SEO defaults.</p>
        </div>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-prepify-600 text-white font-medium px-5 py-2.5 rounded-lg disabled:opacity-50" data-testid="save-settings-btn">
          <Save size={16} /> {saving ? "Saving…" : "Save"}
        </button>
      </header>

      <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h3 className="text-lg font-semibold">Brand</h3>
        <div className="grid grid-cols-2 gap-4">
          <F label="Brand Name"><I value={s.brand_name} onChange={(e) => patch("brand_name", e.target.value)} /></F>
          <F label="Tagline"><I value={s.tagline} onChange={(e) => patch("tagline", e.target.value)} /></F>
          <F label="Logo URL" hint="Square/transparent preferred"><I value={s.logo_url || ""} onChange={(e) => patch("logo_url", e.target.value)} /></F>
          <F label="Favicon URL"><I value={s.favicon_url || ""} onChange={(e) => patch("favicon_url", e.target.value)} /></F>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h3 className="text-lg font-semibold">Contact</h3>
        <div className="grid grid-cols-2 gap-4">
          <F label="Email"><I type="email" value={s.contact_email} onChange={(e) => patch("contact_email", e.target.value)} /></F>
          <F label="Phone"><I value={s.contact_phone} onChange={(e) => patch("contact_phone", e.target.value)} /></F>
        </div>
        <F label="Address"><TA rows={2} value={s.address} onChange={(e) => patch("address", e.target.value)} /></F>
      </section>

      <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h3 className="text-lg font-semibold">Social Links</h3>
        <div className="grid grid-cols-2 gap-4">
          {["instagram", "linkedin", "twitter", "youtube", "facebook"].map((k) => (
            <F key={k} label={k[0].toUpperCase() + k.slice(1)}><I placeholder="https://…" value={s.social?.[k] || ""} onChange={(e) => patchSocial(k, e.target.value)} /></F>
          ))}
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h3 className="text-lg font-semibold">Footer</h3>
        <F label="Footer Note"><I value={s.footer_note} onChange={(e) => patch("footer_note", e.target.value)} /></F>
      </section>

      <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h3 className="text-lg font-semibold">SEO Defaults</h3>
        <F label="Default Page Title"><I value={s.seo.title} onChange={(e) => patchSEO("title", e.target.value)} /></F>
        <F label="Default Meta Description"><TA rows={3} value={s.seo.description} onChange={(e) => patchSEO("description", e.target.value)} /></F>
        <F label="Default OG Image URL"><I value={s.seo.og_image_url || ""} onChange={(e) => patchSEO("og_image_url", e.target.value)} /></F>
        <F label="Keywords (comma-separated)"><I value={(s.seo.keywords || []).join(", ")} onChange={(e) => patchSEO("keywords", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></F>

        <div className="border-t border-slate-100 pt-5 mt-2">
          <h4 className="text-sm font-semibold text-slate-900 mb-1">Search Console & Analytics</h4>
          <p className="text-xs text-slate-500 mb-3">Paste the verification token from each dashboard (no HTML tags — just the content value).</p>
          <F label="Google Search Console verification" hint='From Google Search Console → Add property → HTML meta tag → copy only the value between content=""'>
            <I value={s.seo.google_site_verification || ""} onChange={(e) => patchSEO("google_site_verification", e.target.value)} placeholder="e.g. abc123xyz..." data-testid="gsc-input" />
          </F>
          <F label="Bing Webmaster verification" hint='msvalidate.01 content value'>
            <I value={s.seo.bing_site_verification || ""} onChange={(e) => patchSEO("bing_site_verification", e.target.value)} placeholder="e.g. ABC123..." data-testid="bing-input" />
          </F>
          <F label="Google Analytics (GA4) Measurement ID" hint="Starts with G-XXXXXXXXXX">
            <I value={s.seo.google_analytics_id || ""} onChange={(e) => patchSEO("google_analytics_id", e.target.value)} placeholder="G-XXXXXXXXXX" data-testid="ga4-input" />
          </F>
        </div>

        <div className="border-t border-slate-100 pt-5 mt-2">
          <h4 className="text-sm font-semibold text-slate-900 mb-1">Organization (Structured data)</h4>
          <p className="text-xs text-slate-500 mb-3">Powers rich Google results via schema.org/Organization JSON-LD.</p>
          <F label="Organization name"><I value={s.seo.organization_name || ""} onChange={(e) => patchSEO("organization_name", e.target.value)} data-testid="org-name-input" /></F>
          <F label="Canonical URL" hint="Production URL of your site, e.g. https://prepify.co.in">
            <I value={s.seo.organization_url || ""} onChange={(e) => patchSEO("organization_url", e.target.value)} placeholder="https://prepify.co.in" data-testid="canonical-url-input" />
          </F>
          <F label="Organization logo URL"><I value={s.seo.organization_logo_url || ""} onChange={(e) => patchSEO("organization_logo_url", e.target.value)} data-testid="org-logo-url-input" /></F>
          <F label="Twitter handle" hint="Including @, e.g. @prepify"><I value={s.seo.twitter_handle || ""} onChange={(e) => patchSEO("twitter_handle", e.target.value)} placeholder="@prepify" data-testid="twitter-handle-input" /></F>
        </div>
      </section>
    </div>
  );
};

export default SiteSettings;
